import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../api.service';
import { CookieService } from 'ngx-cookie-service';
import { CommonService } from '../../shared/common.service';
import { HttpParams } from '@angular/common/http';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

interface Data {
  template_id?: string;
  template_name?: string;
  study_id: string;
  study_name?: string;
  subject_id: string;
  subject_name?: string;
  participant_id?: string;
  participantId?: string;
  form_id: string;
  form_name: string;
  folder_name?: string;
  field_id: string;
  field_name: string;
  field_type?: string;
  value: string;
  updated_at?: string;
  site_location: string;
  document_link?: string | null;
  document_name?: string | null;
}

interface TableColumnDefinition {
  name: string;
  title: string;
  type?: string;
  required?: boolean;
  values?: any;
}

@Component({
  selector: 'app-data-view',
  templateUrl: './data-view.component.html',
  styleUrls: ['./data-view.component.scss'],
})
export class DataViewComponent implements OnInit {
  locationFilter: string;
  subjectFilter: string;
  formFilter: string;
  fieldFilter: string;
  studyName: string;
  studyId: number;
  data: Data[] = [];
  filteredData: Data[] = [];
  paginatedData: Data[] = [];
  totalPages: number[] = [];
  currentPage = 1;
  pageSize = 10;
  pages = 0;
  totalUsers = 0;
  authToken: string | null = null;
  previousPage: number;
  pageNumbersToShow: number = 4;
  pageRange: number[] = [];
  maxPagesToShow: number = 4;
  loading: boolean = false;

  // Properties for selected study
  selectedStudyId: string = '';
  selectedStudyName: string = '';
  availableStudies: any[] = [];
  filteredStudies: any[] = [];
  isStudyDropdownOpen: boolean = false;
  studySearchText: string = '';

  // Table popup modal properties
  isTableModalOpen = false;
  selectedTableField: any = null;
  tableModalData: any[] = [];
  tableModalColumns: TableColumnDefinition[] = [];
  tableFieldName: string = '';
  tableSubjectId: string = '';
  isGeneratingPDF: boolean = false;
  private readonly tableColumnsToExclude = new Set([
    'operation',
    'is_new_row',
    'edit_reason',
    'existing_row_id',
  ]);

  private tableColumnCache: Record<string, TableColumnDefinition[]> = {};
  /** Populated from template table-columns API when the response includes field type. */
  private fieldTypeByFieldId: Record<string, string> = {};
  private readonly excludedExportFieldTypes = new Set(['instruction', 'suggestion']);

  private isExcludedFromExport(item: Data): boolean {
    const ft = String(item.field_type || '').trim().toLowerCase();
    if (ft !== '' && this.excludedExportFieldTypes.has(ft)) {
      return true;
    }
    const fieldName = String(item.field_name || '').trim().toLowerCase();
    return fieldName === 'instruction/suggestion';
  }

  /**
   * Returns the currently filtered data WITHOUT UI grouping, preserving backend order.
   * Important for exports where column order must match backend row order.
   */
  private getUngroupedFilteredDataForExport(): Data[] {
    if (!this.data || this.data.length === 0) {
      return [];
    }

    let filtered = [...this.data];

    const selectedFolderIds = this.getSelectedFolderIds();
    const selectedFormIds = this.getSelectedFormIds();

    // Apply location filter
    if (this.locationFilter && this.locationFilter !== '') {
      const needle = this.locationFilter.toLowerCase();
      filtered = filtered.filter(
        (item) => item.site_location && item.site_location.toLowerCase().includes(needle)
      );
    }

    // Apply subject filter
    if (this.subjectFilter && this.subjectFilter !== '') {
      const needle = this.subjectFilter.toLowerCase();
      filtered = filtered.filter(
        (item) => item.subject_id && item.subject_id.toLowerCase().includes(needle)
      );
    }

    // Apply advanced form / folder filters
    if (selectedFormIds.length > 0) {
      const allowedFormIds = new Set(selectedFormIds.map(String));
      filtered = filtered.filter(
        (item) => (item as any).form_id != null && allowedFormIds.has(String((item as any).form_id))
      );
    } else if (selectedFolderIds.length > 0 && this.formOptions.length > 0) {
      const allowedFormIdsFromFolders = new Set(
        this.formOptions
          .filter((form) => form.folderId && selectedFolderIds.includes(form.folderId))
          .map((form) => String(form.id))
      );

      filtered = filtered.filter(
        (item) =>
          (item as any).form_id != null &&
          allowedFormIdsFromFolders.has(String((item as any).form_id))
      );
    } else if (this.formFilter && this.formFilter !== '') {
      const needle = this.formFilter.toLowerCase();
      filtered = filtered.filter(
        (item) => item.form_name && item.form_name.toLowerCase().includes(needle)
      );
    }

    // Apply field filter (checkboxes take precedence; fallback to legacy fieldFilter)
    const selectedFieldNames = this.getSelectedFieldNames();
    if (selectedFieldNames.length > 0) {
      const allowedFields = new Set(selectedFieldNames);
      filtered = filtered.filter((item) => item.field_name && allowedFields.has(item.field_name));
    } else if (this.fieldFilter && this.fieldFilter !== '') {
      const needle = this.fieldFilter.toLowerCase();
      filtered = filtered.filter(
        (item) => item.field_name && item.field_name.toLowerCase().includes(needle)
      );
    }

    return filtered;
  }

  /**
   * Natural sort comparator for Screening IDs like VLCE01, VLCE2, VLCEC02.
   * Splits into alpha/numeric chunks and compares numerically where possible.
   */
  private naturalCompare(a: string, b: string): number {
    const as = String(a ?? '');
    const bs = String(b ?? '');
    if (as === bs) return 0;

    const tokenize = (s: string) =>
      s
        .toUpperCase()
        .match(/[A-Z]+|\d+/g)
        ?.map((tok) => (/\d+/.test(tok) ? Number(tok) : tok)) ?? [s.toUpperCase()];

    const ta = tokenize(as);
    const tb = tokenize(bs);
    const len = Math.max(ta.length, tb.length);

    for (let i = 0; i < len; i++) {
      const xa = ta[i];
      const xb = tb[i];
      if (xa === undefined) return -1;
      if (xb === undefined) return 1;
      if (xa === xb) continue;

      const na = typeof xa === 'number';
      const nb = typeof xb === 'number';
      if (na && nb) return (xa as number) - (xb as number);
      if (!na && !nb) return String(xa).localeCompare(String(xb));

      // If types differ, keep alpha chunks before numeric chunks
      return na ? 1 : -1;
    }

    return as.localeCompare(bs);
  }

  // Filter options
  availableLocations: string[] = [];
  availableSubjects: string[] = [];
  availableForms: string[] = [];
  availableFields: string[] = [];
  formFieldMap: Record<string, string[]> = {};

  // Advanced folder / form filter options (built from template structure)
  templateId: string | null = null;
  template: any = null;

  folderOptions: { id: string; name: string }[] = [];
  formOptions: { id: string; name: string; folderId?: string }[] = [];

  folderCheckboxes: { [id: string]: boolean } = {};
  formCheckboxes: { [id: string]: boolean } = {};
  fieldCheckboxes: { [fieldName: string]: boolean } = {};

  showFolderDropdown: boolean = false;
  showFormDropdown: boolean = false;
  showFieldDropdown: boolean = false;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private cookieService: CookieService,
    private commonService: CommonService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    let userData = this.commonService.getUserData();

    // if (userData == null || userData.designation != 'CRO') {
    //   this.commonService.navigateTo('/');
    // }

    this.authToken = this.cookieService.get('token');
    // console.log('Retrieved Token:', this.authToken);

    // Load available studies
    this.loadAvailableStudies();

    let params = new HttpParams();
    this.route.queryParams.subscribe((params) => {
      this.studyName = params['studyName'] || '';
      this.studyId = parseInt(params['studyId'] || null);
      this.currentPage = +params['page'] || 1;
      this.locationFilter = params['locationFilter'] || '';
      this.subjectFilter = params['subjectFilter'] || '';
      this.formFilter = params['formFilter'] || '';
      this.fieldFilter = params['fieldFilter'] || '';

      // Get selected study from query parameters
      this.selectedStudyId = params['studyId'] || '';
      this.selectedStudyName = params['studyName'] || '';

      if (this.selectedStudyId) {
        this.loadData(this.currentPage);
      }
    });
  }

  loadData(page: number): void {
    this.loading = true;

    const locationFilter = this.locationFilter || '';
    const subjectFilter = this.subjectFilter || '';
    const formFilter = this.formFilter || '';
    const fieldFilter = this.fieldFilter || '';

    const fetchAllRows = async (): Promise<Data[]> => {
      const limit = 5000;
      const all: Data[] = [];
      let p = 1;
      let hasNext = true;

      while (hasNext) {
        const resp: any = await new Promise((resolve, reject) => {
          const sub = this.apiService
            .getStudyData(
              p,
              limit,
              locationFilter,
              subjectFilter,
              formFilter,
              fieldFilter,
              this.studyId
            )
            .subscribe({
              next: (r) => {
                resolve(r);
                sub.unsubscribe();
              },
              error: (e) => {
                reject(e);
                sub.unsubscribe();
              },
            });
        });

        const rows = (resp?.data || []) as Data[];
        all.push(...rows);

        const pg =
          resp?.pagination ??
          resp?.data?.pagination ??
          resp?.meta?.pagination ??
          resp?.pagination?.pagination ??
          null;

        const rawNext = pg?.hasNextPage;
        const hasNextPage =
          typeof rawNext === 'boolean'
            ? rawNext
            : rawNext === 'true' || rawNext === '1'
              ? true
              : rawNext === 'false' || rawNext === '0'
                ? false
                : pg?.currentPage != null && pg?.totalPages != null
                  ? Number(pg.currentPage) < Number(pg.totalPages)
                  : rows.length === limit;

        // Backend may return fewer than `limit` rows after server-side filtering (e.g. empty
        // checkbox rows removed in dataView). Pagination must not require a full batch.
        hasNext = hasNextPage;
        p += 1;

        // Safety guard: large studies can exceed 500 pages (500×5000 SQL rows). Cap very high
        // so we do not silently truncate after ~2.5M rows while still stopping runaway loops.
        if (p > 250000) {
          console.warn('Data view: pagination stopped at safety cap (250000 pages).');
          break;
        }
      }

      return all;
    };

    fetchAllRows()
      .then((rows) => {
        this.data = rows || [];

        // Respect current UI filters (same set as export) while keeping the requested page.
        this.filteredData = this.getUngroupedFilteredDataForExport();

        // Initialize template / folder / form context from the loaded data
        this.initializeTemplateContextFromData();

        // Populate filter options
        this.populateFilterOptions();

        // Update pagination
        this.pages = Math.ceil(this.filteredData.length / this.pageSize);
        this.totalUsers = this.filteredData.length;

        // Update current page data
        this.updatePaginatedData(page);

        this.loading = false;
      })
      .catch((error) => {
        console.error('Error fetching users', error);
        this.loading = false;
      });
  }

  private async fetchTableColumns(fieldId: string): Promise<TableColumnDefinition[]> {
    if (!fieldId) {
      return [];
    }

    if (this.tableColumnCache[fieldId]) {
      return this.tableColumnCache[fieldId];
    }

    try {
      const response = await new Promise<any>((resolve, reject) => {
        const subscription = this.apiService
          .getTemplateTableColumns(fieldId)
          .subscribe({
            next: (data) => {
              resolve(data);
              subscription.unsubscribe();
            },
            error: (err) => {
              reject(err);
              subscription.unsubscribe();
            },
          });
      });

      const dataPayload = response?.data;
      let rawColumns: any[] = [];
      let fieldTypeFromApi: string | undefined;
      if (Array.isArray(dataPayload)) {
        rawColumns = dataPayload;
      } else if (dataPayload && typeof dataPayload === 'object') {
        rawColumns = Array.isArray(dataPayload.columns) ? dataPayload.columns : [];
        fieldTypeFromApi =
          dataPayload.field_type ??
          dataPayload.fieldType ??
          dataPayload.type ??
          dataPayload.field?.type;
      }
      const resolvedFromResponse =
        fieldTypeFromApi ?? response?.field_type ?? response?.fieldType;
      if (fieldId && resolvedFromResponse) {
        this.fieldTypeByFieldId[fieldId] = String(resolvedFromResponse);
      }

      const normalizedColumns: TableColumnDefinition[] = rawColumns.map(
        (column: any, index: number) => {
          const fallbackKey = column.name || column.column_name || `col_${index}`;
          return {
            name: fallbackKey,
            title:
              column.title ||
              column.column_title ||
              this.formatColumnTitle(fallbackKey),
            type: column.type,
            required: column.required,
            values: column.values ?? column.value_config ?? null,
          };
        }
      );

      this.tableColumnCache[fieldId] = normalizedColumns;
      return normalizedColumns;
    } catch (error) {
      console.error('Error fetching table columns:', error);
      this.tableColumnCache[fieldId] = [];
      return [];
    }
  }

  updatePaginatedData(page: number): void {
    const startIndex = (page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedData = this.filteredData.slice(startIndex, endIndex);
    this.updatePageRange();
  }

  openDocument(link: string): void {
    if (!link) {
      return;
    }

    window.open(link, '_blank', 'noopener');
  }

  private parseJsonArrayOfPrimitives(raw: any): string[] | null {
    const s = String(raw ?? '').trim();
    if (!s) return null;
    try {
      const parsed = JSON.parse(s);
      if (!Array.isArray(parsed)) return null;
      const vals = parsed
        .filter((x) => x !== null && x !== undefined && typeof x !== 'object')
        .map((x) => String(x).trim())
        .filter((x) => x !== '');
      return vals;
    } catch {
      return null;
    }
  }

  private isEffectivelyEmptyValue(raw: any): boolean {
    const s = String(raw ?? '').trim();
    if (s === '' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') {
      return true;
    }
    // Treat empty JSON arrays as empty (common for unchecked checkbox fields).
    const arr = this.parseJsonArrayOfPrimitives(s);
    if (arr && arr.length === 0) return true;
    if (s === '[]') return true;
    return false;
  }

  formatValueForDisplay(item: Data): string {
    const raw = item?.value ?? '';
    // Display checkbox JSON arrays nicely in the UI.
    const ft = String(item?.field_type ?? '').trim().toLowerCase();
    if (ft === 'checkbox' || ft === 'checkbox-group') {
      const arr = this.parseJsonArrayOfPrimitives(raw);
      if (arr) return arr.join(', ');
    }
    // Also handle when field_type is missing but value looks like a primitive array.
    const arr = this.parseJsonArrayOfPrimitives(raw);
    if (arr) return arr.join(', ');
    return String(raw ?? '');
  }

  // Group data by field_id to show only one row per field per subject/form/site
  groupDataByFieldId(data: Data[]): Data[] {
    const groupedData = new Map<string, Data>();

    data.forEach((item) => {
      // Include site_location in the key to keep entries with different locations separate
      // IMPORTANT: include folder_name to avoid collisions when the same form/field
      // exists in multiple folders (common in unfiltered views).
      const key = `${item.subject_id}_${item.form_id}_${item.field_id}_${item.site_location}_${item.folder_name}`;

      if (!groupedData.has(key)) {
        groupedData.set(key, { ...item });
      } else {
        const existing = groupedData.get(key)!;
        const existingVal = String(existing.value ?? '').trim();
        const incomingVal = String(item.value ?? '').trim();

        // Always keep table payloads (they can arrive later in the list).
        if (this.isTableData(item.value)) {
          groupedData.set(key, { ...item });
          return;
        }

        // For non-table fields (including checkbox), prefer the first NON-empty value.
        // This prevents cases where an earlier empty record hides a later filled record.
        const existingEmpty = this.isEffectivelyEmptyValue(existingVal);
        const incomingEmpty = this.isEffectivelyEmptyValue(incomingVal);
        if (existingEmpty && !incomingEmpty) {
          groupedData.set(key, { ...item });
        }
      }
    });

    return Array.from(groupedData.values());
  }

  // ### Pagination Functions ### //
  updatePageRange(): void {
    this.pageRange = [];

    const startPage =
      Math.floor((this.currentPage - 1) / this.maxPagesToShow) *
        this.maxPagesToShow +
      1;
    const endPage = Math.min(startPage + this.maxPagesToShow - 1, this.pages);

    for (let i = startPage; i <= endPage; i++) {
      this.pageRange.push(i);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.pages) {
      this.currentPage++;
      this.updatePaginatedData(this.currentPage);
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedData(this.currentPage);
    }
  }

  goToPage(pageIndex: number): void {
    if (pageIndex > 0 && pageIndex <= this.pages) {
      this.currentPage = pageIndex;
      this.updatePaginatedData(this.currentPage);
    }
  }

  // Check if a field value appears to be table data (JSON array or object)
  isTableData(value: string): boolean {
    if (!value || value.trim() === '') return false;

    try {
      const parsed = JSON.parse(value);
      // Check if it's an array with data
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Treat as "table data" only if it looks like rows (array of objects),
        // not primitive arrays (common for checkbox/multi-select values).
        return parsed.some((row) => {
          if (!row || typeof row !== 'object' || Array.isArray(row)) {
            return false;
          }
          return Object.keys(row).length > 0;
        });
      }
      // Check if it's an object that might represent table data
      if (typeof parsed === 'object' && parsed !== null) {
        // Look for common table data indicators
        const keys = Object.keys(parsed);
        return (
          keys.length > 0 &&
          (keys.some(
            (key) =>
              key.includes('row') ||
              key.includes('column') ||
              key.includes('data')
          ) ||
            keys.some(
              (key) =>
                typeof parsed[key] === 'object' || Array.isArray(parsed[key])
            ))
        );
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  // Check if a field has table data
  hasTableData(fieldName: string): boolean {
    return this.data.some(
      (item) => item.field_name === fieldName && this.isTableData(item.value)
    );
  }

  // Open table popup modal with data
  async openTablePopup(
    fieldName: string,
    subjectId: string,
    siteLocation: string
  ): Promise<void> {
    // console.log('Opening table popup for field:', fieldName);

    this.tableFieldName = fieldName;
    this.tableSubjectId = subjectId;

    // Get the field_id and subject_id from the first matching item
    // console.log(this.data);
    const firstMatchingItem = this.data.find(
      (item) =>
        item.field_name === fieldName &&
        item.subject_id === subjectId &&
        this.isTableData(item.value)
    );

    if (!firstMatchingItem) {
      // console.log('No table data found for field:', fieldName);
      return;
    }

    // Get all table data for this specific field_id and subject_id
    const tableDataItems = this.data.filter(
      (item) =>
        item.field_id === firstMatchingItem.field_id &&
        item.subject_id === firstMatchingItem.subject_id &&
        this.isTableData(item.value)
    );

    if (tableDataItems.length === 0) {
      // console.log('No table data found for field:', fieldName);
      return;
    }

    // Collect all rows from all entries
    this.tableModalData = [];
    this.tableModalColumns = [];

    tableDataItems.forEach((item) => {
      try {
        const parsedData = JSON.parse(item.value);

        if (Array.isArray(parsedData)) {
          // Handle array data - each item in array is a separate row
          this.tableModalData = [...this.tableModalData, ...parsedData];
        } else if (typeof parsedData === 'object' && parsedData !== null) {
          // Handle single object data - treat as one row
          this.tableModalData.push(parsedData);
        }
      } catch (error) {
        console.error('Error parsing table data:', error);
      }
    });

    const metadataColumns = await this.fetchTableColumns(
      firstMatchingItem.field_id
    );

    if (metadataColumns.length > 0) {
      this.tableModalColumns = this.mergeColumnsWithRowKeys(
        metadataColumns,
        this.tableModalData
      );
    }

    if (this.tableModalColumns.length === 0) {
      this.tableModalColumns = this.buildColumnsFromRows(this.tableModalData);
    }

    if (this.tableModalColumns.length > 0) {
      this.tableModalData = this.normalizeRowsWithColumns(
        this.tableModalData,
        this.tableModalColumns
      );
    }

    this.isTableModalOpen = true;

    // console.log('Table popup data:', {
    //   fieldName: fieldName,
    //   data: this.tableModalData,
    //   columns: this.tableModalColumns,
    // });
  }

  private mergeColumnsWithRowKeys(
    metadata: TableColumnDefinition[],
    rows: any[]
  ): TableColumnDefinition[] {
    const rowKeys = this.extractRowKeysFromData(rows);

    return metadata
      .map((column, index) => {
        const resolvedKey = rowKeys[index] || column.name || `col_${index}`;
        const normalizedKey = resolvedKey
          ?.toLowerCase()
          .replace(/\s+/g, '_');
        if (
          !resolvedKey ||
          this.tableColumnsToExclude.has(normalizedKey || '')
        ) {
          return null;
        }

        return {
          ...column,
          name: resolvedKey,
          title: column.title || this.formatColumnTitle(resolvedKey),
        };
      })
      .filter((column): column is TableColumnDefinition => Boolean(column));
  }

  private buildColumnsFromRows(rows: any[]): TableColumnDefinition[] {
    const columnOrder: string[] = [];

    rows.forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (key === 'row_index') {
          return;
        }

        const normalizedKey = key.toLowerCase().replace(/\s+/g, '_');
        if (this.tableColumnsToExclude.has(normalizedKey)) {
          return;
        }

        if (!columnOrder.includes(key)) {
          columnOrder.push(key);
        }
      });
    });

    return columnOrder.map((key) => ({
      name: key,
      title: this.formatColumnTitle(key),
    }));
  }

  private extractRowKeysFromData(rows: any[]): string[] {
    const keys: string[] = [];

    rows.forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (key === 'row_index') {
          return;
        }

        const normalizedKey = key.toLowerCase().replace(/\s+/g, '_');
        if (this.tableColumnsToExclude.has(normalizedKey)) {
          return;
        }

        if (!keys.includes(key)) {
          keys.push(key);
        }
      });
    });

    return keys;
  }

  private normalizeRowsWithColumns(
    rows: any[],
    columns: TableColumnDefinition[]
  ): any[] {
    if (!rows.length) {
      return rows;
    }

    return rows.map((row) => {
      const normalizedRow = { ...row };
      columns.forEach((column, index) => {
        const key = column.name || `col_${index}`;
        if (normalizedRow[key] === undefined || normalizedRow[key] === null) {
          normalizedRow[key] = '';
        }
      });
      return normalizedRow;
    });
  }

  private formatColumnTitle(key: string): string {
    if (!key) {
      return '';
    }

    return key
      .replace(/_/g, ' ')
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  // Close table popup modal
  closeTablePopup(): void {
    this.isTableModalOpen = false;
    this.selectedTableField = null;
    this.tableModalData = [];
    this.tableModalColumns = [];
    this.tableFieldName = '';
  }

  // Load available studies for dropdown
  loadAvailableStudies(): void {
    const userData = this.commonService.getUserData();
    if (userData && userData.id) {
      this.apiService.getStudiesCreatedByUser(userData.id).subscribe(
        (response: any) => {
          // console.log('Available studies loaded:', response);
          this.availableStudies = response.data || [];
          this.filteredStudies = [...this.availableStudies];
        },
        (error) => {
          console.error('Error loading available studies:', error);
          this.availableStudies = [];
          this.filteredStudies = [];
        }
      );
    }
  }

  // Toggle study dropdown
  toggleStudyDropdown(): void {
    this.isStudyDropdownOpen = !this.isStudyDropdownOpen;
    if (this.isStudyDropdownOpen) {
      this.filteredStudies = [...this.availableStudies];
      this.studySearchText = '';
    }
  }

  // Filter studies based on search text
  filterStudies(): void {
    if (!this.studySearchText.trim()) {
      this.filteredStudies = [...this.availableStudies];
    } else {
      this.filteredStudies = this.availableStudies.filter((study) => {
        const studyName = (study.name || study.study_name || '').toLowerCase();
        return studyName.includes(this.studySearchText.toLowerCase());
      });
    }
  }

  // Select a study from the dropdown
  selectStudy(study: any): void {
    this.selectedStudyId = study.id || study.study_id;
    this.selectedStudyName = study.name || study.study_name;
    this.isStudyDropdownOpen = false;
    this.studySearchText = '';
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown-container')) {
      this.isStudyDropdownOpen = false;
      this.showFolderDropdown = false;
      this.showFormDropdown = false;
      this.showFieldDropdown = false;
    }
  }

  // Submit study selection
  submitStudySelection(): void {
    if (this.selectedStudyId) {
      this.studyId = parseInt(this.selectedStudyId);
      this.currentPage = 1;
      this.loadData(this.currentPage);

      // Update URL with selected study
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          studyId: this.selectedStudyId,
          studyName: this.selectedStudyName,
          page: this.currentPage,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  // Populate filter dropdowns with available options
  // Populate filter dropdowns with available options
  populateFilterOptions(): void {
    if (!this.data || this.data.length === 0) return;

    // Get unique locations
    this.availableLocations = [
      ...new Set(
        this.data
          .map((item) => {
            const anyItem: any = item as any;
            return (
              anyItem.site_location ||
              anyItem.site_name ||
              anyItem.site ||
              ''
            );
          })
          .filter((location) => typeof location === 'string' && location.trim() !== '')
      ),
    ].sort();

    // Get unique subjects
    this.availableSubjects = [
      ...new Set(
        this.data
          .map((item) => {
            const anyItem: any = item as any;
            return anyItem.subject_id || anyItem.subject_name || '';
          })
          .filter((subject) => typeof subject === 'string' && subject.trim() !== '')
      ),
    ].sort();

    // Get unique forms
    this.availableForms = [
      ...new Set(
        this.data
          .map((item) => {
            const anyItem: any = item as any;
            return anyItem.form_name || anyItem.form || '';
          })
          .filter((form) => typeof form === 'string' && form.trim() !== '')
      ),
    ].sort();

    // Build form to fields map and set default available fields
    const formFieldMap: Record<string, Set<string>> = {};

    this.data.forEach((item) => {
      const formName = item.form_name;
      const fieldName = item.field_name;

      if (!formName || !formName.trim() || !fieldName || !fieldName.trim()) {
        return;
      }

      if (!formFieldMap[formName]) {
        formFieldMap[formName] = new Set<string>();
      }

      formFieldMap[formName].add(fieldName);
    });

    this.formFieldMap = Object.keys(formFieldMap).reduce(
      (accumulator, formName) => {
        accumulator[formName] = Array.from(formFieldMap[formName]).sort();
        return accumulator;
      },
      {} as Record<string, string[]>
    );

    this.updateAvailableFields();

    // Ensure formOptions are available for the checkbox form filter
    if (!this.formOptions || this.formOptions.length === 0) {
      this.buildFormOptionsFromDataFallback();
    }
  }

  /**
   * Load template structure (folders/forms) based on the first data row
   * so that we can build folder / form mappings for advanced filters.
   */
  private initializeTemplateContextFromData(): void {
    if (this.template || !this.data || this.data.length === 0) {
      return;
    }

    const first = this.data[0];
    const templateId =
      (first && (first as any).template_id != null
        ? String((first as any).template_id)
        : null) || null;

    // If we don't have a valid template id, at least build form options from data
    if (!templateId || templateId === 'null' || templateId === 'undefined') {
      this.buildFormOptionsFromDataFallback();
      return;
    }

    this.templateId = templateId;

    this.apiService.getTemplateData(templateId).subscribe(
      (response: any) => {
        if (response && response.template) {
          this.template = response.template;
          this.buildFolderAndFormOptionsFromTemplate();
        } else {
          // Fallback: still try to build form options from data if template is missing
          this.buildFormOptionsFromDataFallback();
        }
      },
      (error) => {
        console.error('Error loading template for data view:', error);
        this.buildFormOptionsFromDataFallback();
      }
    );
  }

  /**
   * Build folder / form options from the loaded template.
   */
  private buildFolderAndFormOptionsFromTemplate(): void {
    this.folderOptions = [];
    this.formOptions = [];
    this.folderCheckboxes = {};
    this.formCheckboxes = {};

    if (!this.template || !this.template.folders) {
      this.buildFormOptionsFromDataFallback();
      return;
    }

    try {
      this.template.folders.forEach((folder: any) => {
        if (!folder || folder.id == null || !folder.name) {
          return;
        }
        const folderId = String(folder.id);
        this.folderOptions.push({ id: folderId, name: folder.name });

        if (Array.isArray(folder.forms)) {
          folder.forms.forEach((form: any) => {
            if (!form || form.id == null || !form.name) {
              return;
            }
            const formId = String(form.id);
            this.formOptions.push({
              id: formId,
              name: form.name,
              folderId,
            });
          });
        }
      });
      this.folderOptions.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
      this.formOptions.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    } catch (error) {
      console.error('Error building folder/form options from template:', error);
      this.folderOptions = [];
      this.formOptions = [];
    }
  }

  /**
   * Fallback if template cannot be loaded: build simple form options from data.
   * Uses folder_name from data to also build folder options.
   */
  private buildFormOptionsFromDataFallback(): void {
    this.formOptions = [];
    this.folderOptions = [];
    this.folderCheckboxes = {};
    this.formCheckboxes = {};

    if (!this.data || this.data.length === 0) {
      return;
    }

    const seenFormIds = new Set<string>();
    const seenFolderNames = new Set<string>();

    this.data.forEach((item) => {
      const folderName = (item as any).folder_name as string | undefined;
      const rawFormId = (item as any).form_id;
      const formName = item.form_name;
      if (rawFormId == null || !formName) {
        return;
      }
      const formId = String(rawFormId);

      // Build folder options from folder_name
      if (folderName && folderName.trim() !== '' && !seenFolderNames.has(folderName)) {
        seenFolderNames.add(folderName);
        // Use folder_name as the id key for filtering
        this.folderOptions.push({ id: folderName, name: folderName });
      }

      // Build form options and link them to folder_name when available
      if (!seenFormIds.has(formId)) {
        seenFormIds.add(formId);
        this.formOptions.push({
          id: formId,
          name: formName,
          folderId: folderName || undefined,
        });
      }
    });

    this.folderOptions.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    this.formOptions.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }

  // Apply filters to data
  applyFilters(): void {
    this.currentPage = 1; // Reset to first page when filters change

    if (!this.data || this.data.length === 0) {
      this.filteredData = [];
      this.paginatedData = [];
      return;
    }

    // Same rules as Excel export so the grid and download always match.
    this.filteredData = this.getUngroupedFilteredDataForExport();

    // Update available fields based on current selection
    this.updateAvailableFields();

    // Update pagination
    this.pages = Math.ceil(this.filteredData.length / this.pageSize);
    this.totalUsers = this.filteredData.length;

    // Update current page data
    this.updatePaginatedData(this.currentPage);
  }

  // Search functionality
  onSearch(): void {
    this.applyFilters();
  }

  // Clear all filters
  clearFilters(): void {
    this.locationFilter = '';
    this.subjectFilter = '';
    this.formFilter = '';
    this.fieldFilter = '';
    this.folderCheckboxes = {};
    this.formCheckboxes = {};
    this.fieldCheckboxes = {};
    this.showFolderDropdown = false;
    this.showFormDropdown = false;
    this.showFieldDropdown = false;
    this.updateAvailableFields();
    this.applyFilters();
  }

  onFormFilterChange(selectedForm: string): void {
    this.formFilter = selectedForm;
    this.updateAvailableFields();

    if (
      this.fieldFilter &&
      !this.availableFields.includes(this.fieldFilter)
    ) {
      this.fieldFilter = '';
    }
  }

  private updateAvailableFields(): void {
    const selectedFormIds = this.getSelectedFormIds();

    if (selectedFormIds.length > 0 && this.data && this.data.length > 0) {
      const allowedFormIds = new Set(selectedFormIds);
      this.availableFields = [
        ...new Set(
          this.data
            .filter(
              (item) =>
                (item as any).form_id != null &&
                allowedFormIds.has(String((item as any).form_id))
            )
            .map((item) => item.field_name)
            .filter((field) => field && field.trim() !== '')
        ),
      ].sort();
    } else if (this.formFilter && this.formFilter.trim() !== '') {
      this.availableFields = [...(this.formFieldMap[this.formFilter] || [])].sort();
    } else if (this.data && this.data.length > 0) {
      this.availableFields = [
        ...new Set(
          this.data
            .map((item) => item.field_name)
            .filter((field) => field && field.trim() !== '')
        ),
      ].sort();
    } else {
      this.availableFields = [];
    }

    // Clear field checkboxes for fields no longer in available list
    const allowedSet = new Set(this.availableFields);
    Object.keys(this.fieldCheckboxes).forEach((name) => {
      if (!allowedSet.has(name)) {
        delete this.fieldCheckboxes[name];
      }
    });
  }

  // ===== Folder / Form checkbox helpers =====

  private getSelectedFolderIds(): string[] {
    return Object.keys(this.folderCheckboxes).filter(
      (id) => this.folderCheckboxes[id]
    );
  }

  private getSelectedFormIds(): string[] {
    return Object.keys(this.formCheckboxes).filter(
      (id) => this.formCheckboxes[id]
    );
  }

  getFolderFilterLabel(): string {
    const selectedFolderIds = this.getSelectedFolderIds();
    if (!selectedFolderIds.length) {
      return 'Filter By Folder';
    }
    if (selectedFolderIds.length === 1) {
      const folder = this.folderOptions.find(
        (f) => f.id === selectedFolderIds[0]
      );
      return folder?.name || '1 folder selected';
    }
    return `${selectedFolderIds.length} folders selected`;
  }

  getFormFilterLabel(): string {
    const selectedFormIds = this.getSelectedFormIds();
    if (!selectedFormIds.length) {
      return 'Filter By Form';
    }
    if (selectedFormIds.length === 1) {
      const form = this.formOptions.find((f) => f.id === selectedFormIds[0]);
      return form?.name || '1 form selected';
    }
    return `${selectedFormIds.length} forms selected`;
  }

  toggleFolderDropdown(): void {
    this.showFolderDropdown = !this.showFolderDropdown;
  }

  toggleFormDropdown(): void {
    this.showFormDropdown = !this.showFormDropdown;
  }

  toggleFolderCheckbox(folderId: string): void {
    this.folderCheckboxes[folderId] = !this.folderCheckboxes[folderId];
  }

  toggleFormCheckbox(formId: string): void {
    this.formCheckboxes[formId] = !this.formCheckboxes[formId];
    this.updateAvailableFields();
  }

  getFilteredFormOptions(): { id: string; name: string; folderId?: string }[] {
    const selectedFolderIds = this.getSelectedFolderIds();
    const options = selectedFolderIds.length
      ? this.formOptions.filter(
          (form) => form.folderId && selectedFolderIds.includes(form.folderId)
        )
      : [...this.formOptions];
    return options.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }

  // ===== Field checkbox helpers =====

  private getSelectedFieldNames(): string[] {
    return Object.keys(this.fieldCheckboxes).filter(
      (name) => this.fieldCheckboxes[name]
    );
  }

  getFieldFilterLabel(): string {
    const selected = this.getSelectedFieldNames();
    if (!selected.length) {
      return 'Filter By Field';
    }
    if (selected.length === 1) {
      return selected[0];
    }
    return `${selected.length} fields selected`;
  }

  toggleFieldDropdown(): void {
    this.showFieldDropdown = !this.showFieldDropdown;
  }

  toggleFieldCheckbox(fieldName: string): void {
    this.fieldCheckboxes[fieldName] = !this.fieldCheckboxes[fieldName];
  }

  getStudyNameForRow(data: Data): string {
    return (data as any).study_name || this.selectedStudyName || '-';
  }

  getParticipantIdForRow(data: Data): string {
    const anyRow: any = data as any;
    return (
      anyRow.participant_id ||
      anyRow.participantId ||
      anyRow.participantID ||
      anyRow.participant ||
      ''
    );
  }

  // Download Excel: one sheet per form name (same form from multiple folders in one sheet), with Study Name
  async downloadCSV(): Promise<void> {
    if (!this.selectedStudyId) {
      alert(
        'No data available to download. Please select a study and ensure data is loaded.'
      );
      return;
    }

    // `loadData` already merges every API page into `this.data`. Export from that cache so
    // download matches what was loaded on Submit (no second slow refetch; includes advanced filters).
    const exportSourceRows = this.getUngroupedFilteredDataForExport();
    if (exportSourceRows.length === 0) {
      alert(
        'No data available to download. Please select a study and ensure data is loaded.'
      );
      return;
    }

    this.isGeneratingPDF = true;

    try {
      const workbook = XLSX.utils.book_new();
      const studyName = this.selectedStudyName || 'Study';

      const safeParseJson = (s: string): any | null => {
        try {
          return JSON.parse(s);
        } catch {
          return null;
        }
      };

      const formatCheckboxValue = (raw: any): string => {
        const s = String(raw ?? '').trim();
        if (s === '' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') {
          return '';
        }
        const parsed = safeParseJson(s);
        if (!Array.isArray(parsed)) {
          return s;
        }
        const primitives = parsed
          .filter((x) => x !== null && x !== undefined && typeof x !== 'object')
          .map((x) => String(x).trim())
          .filter(Boolean);
        return primitives.length > 0 ? primitives.join(', ') : '';
      };

      const isCheckboxLikeFieldType = (fieldType: any): boolean => {
        const ft = String(fieldType ?? '').trim().toLowerCase();
        if (!ft) return false;
        return (
          ft === 'checkbox' ||
          ft === 'check_box' ||
          ft === 'multi-select' ||
          ft === 'multiselect' ||
          ft === 'multi_select' ||
          ft === 'multiple_select'
        );
      };

      /**
       * Detect checkbox-like values even when field_type is missing:
       * - JSON array of primitives: ["A","B"]
       * - JSON array of objects with checked/selected flags
       */
      const isCheckboxLikeValue = (raw: any): boolean => {
        const s = String(raw ?? '').trim();
        if (s === '') return false;
        const parsed = safeParseJson(s);
        if (!parsed) return false;
        if (Array.isArray(parsed)) {
          if (parsed.length === 0) return false;
          // primitives array
          if (parsed.every((x) => x === null || x === undefined || typeof x !== 'object')) {
            return true;
          }
          // object array with checked/selected semantics
          return parsed.some((x) => {
            if (!x || typeof x !== 'object' || Array.isArray(x)) return false;
            const anyX: any = x;
            return (
              anyX.checked === true ||
              anyX.selected === true ||
              anyX.isChecked === true ||
              anyX.isSelected === true
            );
          });
        }
        return false;
      };

      const mergeIntoSingleCell = (existing: string, incoming: string): string => {
        const a = String(existing ?? '').trim();
        const b = String(incoming ?? '').trim();
        if (!a) return b;
        if (!b) return a;

        const toParts = (x: string): string[] =>
          x
            .split(/,\s*/g)
            .map((p) => p.trim())
            .filter(Boolean);

        const parts = [...toParts(a), ...toParts(b)];
        const uniq: string[] = [];
        const seen = new Set<string>();
        for (const p of parts) {
          const key = p.toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            uniq.push(p);
          }
        }
        return uniq.join(', ');
      };

      const isEffectivelyEmptyExportValue = (
        existing: any,
        checkboxLike: boolean
      ): boolean => {
        const s = String(existing ?? '').trim();
        if (s === '' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') {
          return true;
        }
        if (!checkboxLike) {
          return false;
        }
        // For checkbox-like fields, treat empty arrays as empty so later selections can win.
        if (s === '[]') return true;
        const parsed = safeParseJson(s);
        if (Array.isArray(parsed)) {
          const primitives = parsed
            .filter((x) => x !== null && x !== undefined && typeof x !== 'object')
            .map((x) => String(x).trim())
            .filter((x) => x !== '');
          return primitives.length === 0;
        }
        return false;
      };

      // Group filtered data by form_name (same form from different folders → one sheet)
      const byFormName = new Map<string, Data[]>();
      exportSourceRows.forEach((item) => {
        const formName = item.form_name || 'Unknown Form';
        if (!byFormName.has(formName)) {
          byFormName.set(formName, []);
        }
        byFormName.get(formName)!.push(item);
      });

      // One sheet per form name
      byFormName.forEach((rows, formName) => {
        // Exclude table data rows from the normal sheets since they are exported separately
        const nonTableRows = rows.filter(
          (item) =>
            // Checkbox payloads are sometimes JSON arrays of objects and can be misdetected as table data.
            // Always keep checkbox-like fields in the main sheet.
            (!this.isTableData(item.value) ||
              isCheckboxLikeFieldType(item.field_type) ||
              isCheckboxLikeValue(item.value)) &&
            !this.isExcludedFromExport(item)
        );
        if (nonTableRows.length === 0) {
          return;
        }

        // Pivot: field names become columns (one row per subject/site/folder/form).
        // Keep field columns in backend order of appearance (not alphabetical),
        // so the Excel columns match exactly what the backend returns.
        const fieldNameFirstIndex = new Map<string, number>();
        for (let i = 0; i < nonTableRows.length; i++) {
          const n = (nonTableRows[i].field_name || '').trim();
          if (n && !fieldNameFirstIndex.has(n)) {
            fieldNameFirstIndex.set(n, i);
          }
        }

        const parseLeadingQuestionNumber = (s: string): number | null => {
          // Matches: "1.", "1)", "1 -", "1\t", etc. (common questionnaire prefixes)
          const m = String(s ?? '').trim().match(/^(\d+)\s*(?:[.)-]|\s)/);
          if (!m) return null;
          const n = Number(m[1]);
          return Number.isFinite(n) ? n : null;
        };

        const fieldNames = Array.from(fieldNameFirstIndex.keys()).sort((a, b) => {
          const na = parseLeadingQuestionNumber(a);
          const nb = parseLeadingQuestionNumber(b);
          if (na != null && nb != null && na !== nb) {
            return na - nb;
          }
          // If one/both don't have a leading number (or equal), preserve backend appearance order.
          return (fieldNameFirstIndex.get(a) ?? 0) - (fieldNameFirstIndex.get(b) ?? 0);
        });

        const metaHeaders = [
          '#',
          'Study Name',
          'Screening ID',
          'Participant ID',
          'Site Location',
          'Folder Name',
          'Form Name',
        ];
        const headers = [...metaHeaders, ...fieldNames];

        type PivotRow = {
          __study_name: string;
          __participant_id: string;
          __subject_id: string;
          __site_location: string;
          __folder_name: string;
          __form_name: string;
          values: Record<string, string>;
        };

        const pivot = new Map<string, PivotRow>();
        const getPivotKey = (r: Data) => {
          const study = this.getStudyNameForRow(r) || studyName || 'N/A';
          const pid = this.getParticipantIdForRow(r) || 'N/A';
          const subj = r.subject_id || 'N/A';
          const site = r.site_location || 'N/A';
          const folder = r.folder_name || 'N/A';
          const form = r.form_name || 'N/A';
          // Keep key in the same order as exported meta headers (Screening ID then Participant ID)
          return [study, subj, pid, site, folder, form].join('|');
        };

        for (const r of nonTableRows) {
          const key = getPivotKey(r);
          if (!pivot.has(key)) {
            pivot.set(key, {
              __study_name: this.getStudyNameForRow(r) || studyName || 'N/A',
              __participant_id: this.getParticipantIdForRow(r) || 'N/A',
              __subject_id: r.subject_id || 'N/A',
              __site_location: r.site_location || 'N/A',
              __folder_name: r.folder_name || 'N/A',
              __form_name: r.form_name || 'N/A',
              values: {},
            });
          }
          const pr = pivot.get(key)!;
          const field = (r.field_name || '').trim();
          if (!field) continue;
          const isCheckboxLike =
            isCheckboxLikeFieldType(r.field_type) || isCheckboxLikeValue(r.value);
          const normalizedVal = isCheckboxLike
            ? formatCheckboxValue(r.value)
            : String(r.value ?? '').toString();

          // If the same field appears multiple times for the same subject/site/folder/form,
          // prefer the first non-empty value (treat checkbox `[]` as empty).
          if (isEffectivelyEmptyExportValue(pr.values[field], isCheckboxLike)) {
            pr.values[field] = normalizedVal;
          }
        }

        const pivotRows = Array.from(pivot.values()).sort((a, b) => {
          // Screening ID chronology first
          const subjectA = String(a.__subject_id || '');
          const subjectB = String(b.__subject_id || '');
          const subjectCmp = this.naturalCompare(subjectA, subjectB);
          if (subjectCmp !== 0) return subjectCmp;

          const folderA = String(a.__folder_name || '');
          const folderB = String(b.__folder_name || '');
          const folderCmp = folderA.localeCompare(folderB, undefined, {
            sensitivity: 'base',
            numeric: true,
          });
          if (folderCmp !== 0) return folderCmp;

          const siteA = String(a.__site_location || '');
          const siteB = String(b.__site_location || '');
          return siteA.localeCompare(siteB, undefined, {
            sensitivity: 'base',
            numeric: true,
          });
        });

        const sheetRows = pivotRows.map((r, i) => [
          i + 1,
          r.__study_name || studyName || 'N/A',
          r.__subject_id || 'N/A',
          r.__participant_id || 'N/A',
          r.__site_location || 'N/A',
          r.__folder_name || 'N/A',
          r.__form_name || 'N/A',
          ...fieldNames.map((f) => r.values[f] ?? ''),
        ]);

        const sheetData = [headers, ...sheetRows];
        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

        // Make long headers readable (basic width auto-fit, capped).
        const widths = headers.map((h, idx) => {
          let maxLen = String(h ?? '').length;
          for (let r = 0; r < Math.min(sheetRows.length, 200); r++) {
            const v = sheetRows[r]?.[idx];
            if (v != null) {
              maxLen = Math.max(maxLen, String(v).length);
            }
          }
          // Cap width so wide text doesn't explode the sheet.
          return { wch: Math.min(Math.max(12, maxLen + 2), 45) };
        });
        (worksheet as any)['!cols'] = widths;

        const safeSheetName = formName.replace(/[\\/*?:\[\]]/g, '_').substring(0, 31);
        let finalSheetName = safeSheetName;
        let sheetCounter = 1;
        while (workbook.SheetNames.includes(finalSheetName)) {
          finalSheetName = `${safeSheetName.substring(0, 28)}_${sheetCounter}`;
          sheetCounter++;
        }
        XLSX.utils.book_append_sheet(workbook, worksheet, finalSheetName);
      });

      // Table data sheets (merged): same form name + field name + same columns -> single sheet,
      // even if folder differs (folder is included as a column in the sheet).
      type TableSheetGroup = {
        formName: string;
        fieldName: string;
        columns: TableColumnDefinition[];
        rows: any[];
        isFixedRowsTable: boolean;
      };

      const tableGroups = new Map<string, TableSheetGroup>();

      // Build a set of table field_ids from filteredData to keep export scoped to current filters
      const filteredTableFieldIds = new Set(
        exportSourceRows
          .filter((item) => this.isTableData(item.value))
          .map((item) => item.field_id)
          .filter((id) => Boolean(id))
      );

      const tableSourceItems = exportSourceRows.filter(
        (item) => filteredTableFieldIds.has(item.field_id) && this.isTableData(item.value)
      );

      for (const item of tableSourceItems) {
        // Parse row objects for this table field entry
        const parsedRows: any[] = [];
        try {
          const parsedData = JSON.parse(item.value);
          if (Array.isArray(parsedData)) {
            parsedRows.push(...parsedData);
          } else if (typeof parsedData === 'object' && parsedData !== null) {
            parsedRows.push(parsedData);
          }
        } catch (error) {
          console.error('Error parsing table data:', error);
        }

        if (parsedRows.length === 0) {
          continue;
        }

        // Resolve columns for this table field_id
        let columns: TableColumnDefinition[] = [];
        try {
          columns = await this.fetchTableColumns(item.field_id);
          if (columns.length === 0) {
            columns = this.buildColumnsFromRows(parsedRows);
          } else {
            columns = this.mergeColumnsWithRowKeys(columns, parsedRows);
          }
        } catch (error) {
          columns = this.buildColumnsFromRows(parsedRows);
        }

        const normalizedRows = this.normalizeRowsWithColumns(parsedRows, columns);
        const columnSignature = columns
          .map((col) => (col.title || col.name || '').toLowerCase().trim())
          .join('|');
        const formNameValue = item.form_name || 'N/A';
        const fieldNameValue = item.field_name || 'N/A';
        const groupKey = `${formNameValue}__${fieldNameValue}__${columnSignature}`;
        const resolvedFieldType =
          item.field_type || this.fieldTypeByFieldId[item.field_id] || '';
        const isFixedRowsTable = resolvedFieldType === 'fixed-rows-table';

        if (!tableGroups.has(groupKey)) {
          tableGroups.set(groupKey, {
            formName: formNameValue,
            fieldName: fieldNameValue,
            columns,
            rows: [],
            isFixedRowsTable,
          });
        }

        const group = tableGroups.get(groupKey)!;
        if (isFixedRowsTable) {
          group.isFixedRowsTable = true;
        }

        // Add rows with metadata columns per row (so merging across folders/subjects/sites works)
        const studyNameValue = this.getStudyNameForRow(item);
        const participantIdValue = this.getParticipantIdForRow(item) || 'N/A';
        const subjectNameValue = item.subject_name || item.subject_id || 'N/A';
        const siteLocationValue = item.site_location || 'N/A';
        const folderNameValue = item.folder_name || 'N/A';

        normalizedRows.forEach((row) => {
          group.rows.push({
            __study_name: studyNameValue,
            __participant_id: participantIdValue,
            __subject_name: subjectNameValue,
            __site_location: siteLocationValue,
            __folder_name: folderNameValue,
            __form_name: formNameValue,
            __field_name: fieldNameValue,
            ...row,
          });
        });
      }

      // Create one sheet per merged group
      tableGroups.forEach((group) => {
        this.createMergedTableSheet(
          workbook,
          group.rows,
          group.columns,
          group.formName,
          group.fieldName,
          studyName,
          group.isFixedRowsTable
        );
      });

      const fileName = `Study_Data_${studyName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      this.isGeneratingPDF = false;
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Error generating Excel. Please try again.');
      this.isGeneratingPDF = false;
    }
  }

  /** Groups table rows that belong to one subject/site/folder submission. */
  private getTableSubjectGroupKey(row: any): string {
    return [
      row.__study_name,
      row.__subject_name,
      row.__participant_id,
      row.__site_location,
      row.__folder_name,
      row.__form_name,
      row.__field_name,
    ].join('|');
  }

  private sortRowsWithinTableGroup(
    rows: any[],
    columns: TableColumnDefinition[]
  ): any[] {
    if (rows.length <= 1) {
      return [...rows];
    }
    if (rows.some((r) => r.row_index != null && r.row_index !== '')) {
      return [...rows].sort(
        (a, b) => (Number(a.row_index) || 0) - (Number(b.row_index) || 0)
      );
    }
    const srCol = columns.find(
      (c) =>
        /sr\.?\s*no/i.test((c.title || '').trim()) ||
        (c.name || '').toLowerCase().replace(/\s+/g, '_') === 'sr_no'
    );
    if (srCol) {
      return [...rows].sort((a, b) => {
        const na = parseFloat(String(a[srCol.name] ?? '').replace(/\D/g, '')) || 0;
        const nb = parseFloat(String(b[srCol.name] ?? '').replace(/\D/g, '')) || 0;
        return na - nb;
      });
    }
    return [...rows];
  }

  // Helper method to create a merged sheet for table data
  private createMergedTableSheet(
    workbook: XLSX.WorkBook,
    tableRows: any[],
    columns: TableColumnDefinition[],
    formName: string,
    fieldName: string,
    studyName?: string,
    isFixedRowsTable: boolean = false
  ): void {
    if (tableRows.length === 0 || columns.length === 0) {
      return;
    }

    const sortedRows = [...tableRows].sort((a, b) => {
      // Screening ID chronology first
      const subjectA = String(a.__subject_name || '');
      const subjectB = String(b.__subject_name || '');
      const subjectCmp = this.naturalCompare(subjectA, subjectB);
      if (subjectCmp !== 0) return subjectCmp;

      const folderA = String(a.__folder_name || '');
      const folderB = String(b.__folder_name || '');
      const folderCmp = folderA.localeCompare(folderB, undefined, {
        sensitivity: 'base',
        numeric: true,
      });
      if (folderCmp !== 0) return folderCmp;

      const siteA = String(a.__site_location || '');
      const siteB = String(b.__site_location || '');
      return siteA.localeCompare(siteB, undefined, {
        sensitivity: 'base',
        numeric: true,
      });
    });

    const baseSheetName = `${formName}_${fieldName}`
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .substring(0, 31);

    const metaHeaders = [
      'Study Name',
      'Screening ID',
      'Participant ID',
      'Site Location',
      'Folder Name',
      'Form Name',
      'Field Name',
    ];

    let worksheetData: any[][];

    if (isFixedRowsTable) {
      const grouped = new Map<string, any[]>();
      for (const row of sortedRows) {
        const k = this.getTableSubjectGroupKey(row);
        if (!grouped.has(k)) {
          grouped.set(k, []);
        }
        grouped.get(k)!.push(row);
      }

      const groupKeys = Array.from(grouped.keys()).sort((a, b) => {
        // group key format:
        // study|screeningId|participant|site|folder|form|field
        const pa = a.split('|');
        const pb = b.split('|');
        const screeningA = pa[1] || '';
        const screeningB = pb[1] || '';
        const screeningCmp = this.naturalCompare(screeningA, screeningB);
        if (screeningCmp !== 0) return screeningCmp;

        const folderA = pa[4] || '';
        const folderB = pb[4] || '';
        const folderCmp = folderA.localeCompare(folderB, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        if (folderCmp !== 0) return folderCmp;

        const siteA = pa[3] || '';
        const siteB = pb[3] || '';
        return siteA.localeCompare(siteB, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
      });

      let maxTableRows = 0;
      const groupRowsSorted = new Map<string, any[]>();
      for (const key of groupKeys) {
        const g = this.sortRowsWithinTableGroup(grouped.get(key)!, columns);
        groupRowsSorted.set(key, g);
        maxTableRows = Math.max(maxTableRows, g.length);
      }
      // For fixed-row tables, keep a single stable header for all subjects.
      // Do NOT derive row headers from any subject's values (e.g. "Sr No"),
      // otherwise headers can become "row1/row2/..." depending on the first subject.
      const rowHeaders = Array.from(
        { length: maxTableRows },
        (_, i) => `Row ${i + 1}`
      );

      // Some backend payloads include a "row label" COLUMN (e.g. "123") whose values are:
      // row1, row2, row3... This represents row headers for the fixed-rows table and can
      // be study-specific, but it repeats for every subject. Export it ONCE at the top.
      const firstGroupRows =
        groupKeys.length > 0 ? groupRowsSorted.get(groupKeys[0]) ?? [] : [];
      const isRowLabelValue = (v: any, expectedIndex: number): boolean => {
        const s = String(v ?? '').trim().toLowerCase();
        if (!s) return false;
        const m = s.match(/^row\s*(\d+)$/);
        if (!m) return false;
        const n = Number(m[1]);
        return Number.isFinite(n) && n === expectedIndex + 1;
      };
      const looksLikeRowLabelColumn = (colName: string): boolean => {
        if (!colName || firstGroupRows.length === 0) return false;
        let matches = 0;
        let seenNonEmpty = 0;
        for (let i = 0; i < Math.min(maxTableRows, firstGroupRows.length); i++) {
          const v = firstGroupRows[i]?.[colName];
          const s = String(v ?? '').trim();
          if (s !== '') {
            seenNonEmpty++;
          }
          if (isRowLabelValue(v, i)) {
            matches++;
          }
        }
        // If most non-empty cells match rowN in order, treat as label column.
        if (seenNonEmpty === 0) return false;
        return matches >= Math.max(2, Math.ceil(seenNonEmpty * 0.8));
      };

      const rowLabelColumn = columns.find((c) => looksLikeRowLabelColumn(c.name)) || null;
      /**
       * Fixed-row tables commonly include a descriptor column like "Statement"/"Question"
       * whose values are identical for every subject. Export these ONCE at the top.
       */
      const looksLikeStaticSharedColumn = (colName: string): boolean => {
        if (!colName || firstGroupRows.length === 0 || groupKeys.length < 2) return false;
        if (rowLabelColumn && colName === rowLabelColumn.name) return false;

        const maxGroupsToCheck = Math.min(groupKeys.length, 10);
        let comparable = 0;
        let matches = 0;

        for (let gi = 1; gi < maxGroupsToCheck; gi++) {
          const g = groupRowsSorted.get(groupKeys[gi]) ?? [];
          const limit = Math.min(maxTableRows, firstGroupRows.length, g.length);
          for (let ri = 0; ri < limit; ri++) {
            const a = String(firstGroupRows[ri]?.[colName] ?? '').trim();
            const b = String(g[ri]?.[colName] ?? '').trim();
            if (a === '' && b === '') continue;
            comparable++;
            if (a === b) matches++;
          }
        }

        if (comparable === 0) return false;
        // If most comparable cells match across subjects, treat as shared.
        return matches >= Math.max(3, Math.ceil(comparable * 0.85));
      };

      const staticSharedColumns = columns.filter((c) => looksLikeStaticSharedColumn(c.name));
      const excludedNames = new Set<string>([
        ...(rowLabelColumn ? [rowLabelColumn.name] : []),
        ...staticSharedColumns.map((c) => c.name),
      ]);
      const exportColumns = columns.filter((c) => !excludedNames.has(c.name));

      const headers = [...metaHeaders, 'Column', ...rowHeaders];
      const rowsOut: any[][] = [];

      // Emit the row-label column ONCE (no subject-specific metadata).
      if (rowLabelColumn) {
        const cells: any[] = [];
        for (let i = 0; i < maxTableRows; i++) {
          cells.push(
            firstGroupRows[i] ? (firstGroupRows[i][rowLabelColumn.name] ?? '') : ''
          );
        }
        rowsOut.push([
          ...metaHeaders.map(() => ''),
          rowLabelColumn.title || rowLabelColumn.name || '',
          ...cells,
        ]);
      }

      // Emit static shared columns ONCE at the top (e.g. "Statement").
      for (const col of staticSharedColumns) {
        const cells: any[] = [];
        for (let i = 0; i < maxTableRows; i++) {
          cells.push(firstGroupRows[i] ? (firstGroupRows[i][col.name] ?? '') : '');
        }
        rowsOut.push([
          ...metaHeaders.map(() => ''),
          col.title || col.name || '',
          ...cells,
        ]);
      }

      for (const key of groupKeys) {
        const g = groupRowsSorted.get(key)!;
        const r0 = g[0];
        for (const col of exportColumns) {
          const metaVals = [
            r0.__study_name || studyName || 'N/A',
            r0.__subject_name || 'N/A',
            r0.__participant_id || 'N/A',
            r0.__site_location || 'N/A',
            r0.__folder_name || 'N/A',
            r0.__form_name || formName || 'N/A',
            r0.__field_name || fieldName || 'N/A',
          ];
          const colLabel = col.title || col.name || '';
          const cells: any[] = [];
          for (let i = 0; i < maxTableRows; i++) {
            cells.push(g[i] ? (g[i][col.name] ?? '') : '');
          }
          rowsOut.push([...metaVals, colLabel, ...cells]);
        }
      }

      worksheetData = [headers, ...rowsOut];
    } else {
      const headers = [...metaHeaders, 'Sr. No', ...columns.map((col) => col.title)];
      const grouped = new Map<string, any[]>();
      for (const row of sortedRows) {
        const k = this.getTableSubjectGroupKey(row);
        if (!grouped.has(k)) {
          grouped.set(k, []);
        }
        grouped.get(k)!.push(row);
      }

      const groupKeys = Array.from(grouped.keys()).sort((a, b) => {
        const pa = a.split('|');
        const pb = b.split('|');
        const screeningA = pa[1] || '';
        const screeningB = pb[1] || '';
        const screeningCmp = this.naturalCompare(screeningA, screeningB);
        if (screeningCmp !== 0) return screeningCmp;

        const folderA = pa[4] || '';
        const folderB = pb[4] || '';
        const folderCmp = folderA.localeCompare(folderB, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        if (folderCmp !== 0) return folderCmp;

        const siteA = pa[3] || '';
        const siteB = pb[3] || '';
        return siteA.localeCompare(siteB, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
      });

      const rowsOut: any[][] = [];
      let sr = 1;
      for (const key of groupKeys) {
        const g = this.sortRowsWithinTableGroup(grouped.get(key)!, columns);
        for (const row of g) {
          rowsOut.push([
            row.__study_name || studyName || 'N/A',
            row.__subject_name || 'N/A',
            row.__participant_id || 'N/A',
            row.__site_location || 'N/A',
            row.__folder_name || 'N/A',
            row.__form_name || formName || 'N/A',
            row.__field_name || fieldName || 'N/A',
            sr++,
            ...columns.map((col) => row[col.name] || ''),
          ]);
        }
      }

      worksheetData = [headers, ...rowsOut];
    }

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    let finalSheetName = baseSheetName;
    let sheetCounter = 1;
    while (workbook.SheetNames.includes(finalSheetName)) {
      finalSheetName = `${baseSheetName.substring(0, 28)}_${sheetCounter}`;
      sheetCounter++;
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, finalSheetName);
  }

  exportTableDataAsCSV(): void {
    if (!this.tableModalData || this.tableModalData.length === 0) {
      alert('No table data available to export.');
      return;
    }

    try {
      // Get table name and date for filename
      const fileName = `${this.tableFieldName.replace(/[^a-zA-Z0-9]/g, '_')}_${
        new Date().toISOString().split('T')[0]
      }.csv`;

      // Extract headers from table columns
      const headers = this.tableModalColumns.map((col) => col.title);

      // Map rows
      const rows = this.tableModalData.map((row, index) => {
        const rowValues = this.tableModalColumns.map(
          (col) => row[col.name] || ''
        );
        return [index + 1, ...rowValues];
      });

      // Add Sr. No header
      const csvContent = [['Sr. No', ...headers], ...rows]
        .map((e) => e.map((v) => `"${v}"`).join(','))
        .join('\n');

      // Create and download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // console.log('Table data exported as CSV:', fileName);
    } catch (error) {
      console.error('Error exporting table data:', error);
      alert('Error exporting table data. Please try again.');
    }
  }
}
